import { useContext, useMemo, useEffect, useState, useCallback } from 'react'
import styled from '@emotion/styled'
import DownloadIcon from '@mui/icons-material/GetApp'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  EnhancedTable,
  CvsListPickerToolbar,
  CvsPaginationFooter,
  CvsNoCheckmarkHeader,
  CvsNoCheckboxRow,
} from '@components'
import { reportContext, submissionContext } from '@contexts'
import { colors } from '@global/constants'
import { showSnackbar } from 'functions'
import { t } from '@lingui/core/macro'

const FlexSpaceBetween = styled.div`
  display: inline-flex;
  justify-content: space-between;
  width: 100%;
`
export const TemplateReport = () => {
  const dispatch = useDispatch()
  const { templates, fetchTemplateReports, downloadReport } = useContext(reportContext)
  const { byId, fetchSubmissionsByTemplateId } = useContext(submissionContext)
  const [selectedTemplateId, setSelectedTemplateId] = useState()
  const templateOptions = useMemo(
    () => templates.map(template => ({ label: template.name, value: template.id })),
    [templates],
  )
  const history = useHistory()

  useEffect(() => {
    setSelectedTemplateId(templates[0] ? templates[0].id : '')
  }, [templates, setSelectedTemplateId])

  useEffect(() => {
    fetchTemplateReports()
  }, [fetchTemplateReports])

  const submissions = useMemo(() => {
    return byId[selectedTemplateId] || []
  }, [byId, selectedTemplateId])

  const download = useCallback(() => {
    downloadReport('submissions', selectedTemplateId)
    dispatch(
      showSnackbar({
        type: 'INFO',
        message: t`Your report is being generated. Depending on the size of your report this may take some time.`,
        duration: 5000,
      }),
    )
  }, [selectedTemplateId, downloadReport])

  const onFilterChange = f => {
    f && setSelectedTemplateId(f)
    f && fetchSubmissionsByTemplateId(f)
  }

  const Toolbar = useCallback(
    () => CvsListPickerToolbar({ filterChanged: onFilterChange, options: templateOptions }),
    [templateOptions],
  )

  const columns = useMemo(
    () => [
      { label: t`Submission Name`, width: '35%', lookup: 'identifier' },
      { label: t`Submitted By`, width: '25%', lookup: 'user_name' },
      {
        label: t`Submitted`,
        width: '25%',
        type: 'date',
        lookup: 'updated_at',
      },
      {
        label: t`Rows`,
        width: '15%',
        type: 'number',
        lookup: 'codes',
      },
    ],
    [],
  )

  const handleRowClick = useCallback(
    id => id && history.push(`/codes/details/${id}/data`),
    [history],
  )

  return (
    <>
      <FlexSpaceBetween>
        <div style={{ paddingTop: 12 }}>
          <div
            style={{
              height: '60px',
              width: '140px',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h6"
              style={{ paddingTop: '15px', paddingLeft: '28px', width: '160px' }}
            >
              {t`Template report`}
            </Typography>
          </div>
        </div>
        <div style={{ padding: '28px 12px 12px 12px' }}>
          <Button
            name="template-report-download"
            color="secondary"
            onClick={download}
            variant="contained"
            style={{ color: colors.white, padding: '5px 0px 5px 7px' }}
            startIcon={<DownloadIcon />}
          />
        </div>
      </FlexSpaceBetween>
      <EnhancedTable
        debug="template"
        FooterComponent={CvsPaginationFooter}
        ToolbarComponent={Toolbar}
        HeaderComponent={CvsNoCheckmarkHeader}
        RowComponent={CvsNoCheckboxRow}
        onFilterChange={() => {}}
        blockMultipleEdit
        keyField="dataset_id"
        orderBy="name"
        order="asc"
        columns={columns}
        handleRowClick={handleRowClick}
        rows={submissions}
        noAdd
      />
    </>
  )
}
